import { useEffect, useState } from "react";

import { useSearchParams } from "react-router-dom";

import toast from "react-hot-toast";

import { getTenant } from "../../../utils/tenantManager";

import {
  createWeightTestApi,
  deleteWeightTestApi,
  getWeightTestByIdApi,
  getWeightTestsApi,
  updateWeightTestApi,
} from "../../../services/operation/weighttest/weightTestService";

export const useWeightTestViewModel = () => {
  const tenantId = getTenant();

  const [searchParams] = useSearchParams();

  const [weightTests, setWeightTests] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(() =>
    Number(searchParams.get("page") ?? "1"),
  );

  const [search, setSearch] = useState(() => searchParams.get("search") ?? "");

  const [totalCount, setTotalCount] = useState(0);

  // ================= LOAD =================
  const loadWeightTests = async () => {
    try {
      setLoading(true);

      const res = await getWeightTestsApi(page, 10, search, tenantId!);

      setWeightTests(res?.data?.data || []);

      setTotalCount(res?.data?.totalCount || 0);
    } catch {
      toast.error("Failed to load weight tests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams();

    params.set("page", page.toString());

    if (search) {
      params.set("search", search);
    }

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params.toString()}`,
    );
  }, [page, search]);

  useEffect(() => {
    loadWeightTests();
  }, [page, search]);

  // ================= GET ONE =================
  const getWeightTestById = async (id: string) => {
    try {
      const res = await getWeightTestByIdApi(id, tenantId!);

      return res?.data;
    } catch {
      toast.error("Failed to load weight test");
    }
  };

  // ================= CREATE =================
  const createWeightTest = async (data: any) => {
    try {
      const res = await createWeightTestApi(data, tenantId!);

      if (res?.success) {
        toast.success("Weight test created");

        loadWeightTests();

        return true;
      }

      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Create failed");

      return false;
    }
  };

  // ================= UPDATE =================
  const updateWeightTest = async (data: any) => {
    try {
      const res = await updateWeightTestApi(data, tenantId!);

      if (res?.success) {
        toast.success("Weight test updated");

        loadWeightTests();

        return true;
      }

      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");

      return false;
    }
  };

  // ================= DELETE =================
  const deleteWeightTest = async (id: string) => {
    try {
      const res = await deleteWeightTestApi(id, tenantId!);

      if (res?.success) {
        loadWeightTests();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  return {
    weightTests,
    loading,

    page,
    setPage,

    totalCount,

    search,
    setSearch,

    loadWeightTests,

    getWeightTestById,

    createWeightTest,
    updateWeightTest,
    deleteWeightTest,
  };
};
